<script setup lang="ts">
import { computed, onMounted } from 'vue'
import AiAssistant from './components/AiAssistant.vue'
import AppFooter from './components/AppFooter.vue'
import AppHeader from './components/AppHeader.vue'
import CollectionProtectionSection from './components/CollectionProtectionSection.vue'
import ContactSection from './components/ContactSection.vue'
import FloatingPlayCta from './components/FloatingPlayCta.vue'
import HeroSection from './components/HeroSection.vue'
import ProcessSection from './components/ProcessSection.vue'
import StoreSection from './components/StoreSection.vue'
import TransportSection from './components/TransportSection.vue'
import VideoDebugPanel from './components/VideoDebugPanel.vue'
import { restoreLocale, useSiteConfig } from './composables/useSiteConfig'
import { trackVisit } from './utils/track'

const {
  config,
  locale,
  copy,
  loading,
  error,
  loadConfig,
  loadOverrides,
  setLocale,
  installRuntimeApi,
} = useSiteConfig()

const themeVariables = computed(() => {
  const theme = config.value?.theme
  if (!theme) return {}
  return {
    '--color-accent': theme.accent,
    '--color-accent-soft': theme.accentSoft,
    '--color-ink': theme.ink,
    '--color-paper': theme.paper,
    '--color-muted': theme.muted,
  }
})

const videoDebugEnabled = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('debug') === '1'

onMounted(async () => {
  installRuntimeApi()
  restoreLocale()
  await loadConfig()
  setLocale(locale.value)
  // 后台覆盖配置与访问打点都不阻塞首屏
  void loadOverrides()
  void trackVisit(config.value, locale.value)
})
</script>

<template>
  <div class="site-shell" :style="themeVariables">
    <div v-if="loading && !config" class="status-screen">
      <span class="status-screen__spinner"></span>
      <p>Loading Rarecard…</p>
    </div>

    <div v-else-if="error || !config || !copy" class="status-screen status-screen--error">
      <h1>页面配置加载失败</h1>
      <p>{{ error ?? '未找到有效配置。' }}</p>
      <button type="button" @click="loadConfig()">重新加载</button>
    </div>

    <template v-else>
      <AppHeader :config="config" :copy="copy" :locale="locale" @locale-change="setLocale" />
      <main>
        <HeroSection :config="config" :copy="copy" />
        <ProcessSection
          :steps="config.processSteps"
          :copy="copy"
          :locale="locale"
          :fallback-locale="config.defaultLocale"
          :watermark-image="config.media.storeWatermark"
        />
        <CollectionProtectionSection
          :methods="config.protectionMethods"
          :copy="copy"
          :locale="locale"
          :fallback-locale="config.defaultLocale"
          :watermark-image="config.media.storeWatermark"
        />
        <StoreSection
          :stores="config.stores"
          :copy="copy"
          :locale="locale"
          :fallback-locale="config.defaultLocale"
          :watermark-image="config.media.storeWatermark"
        />
        <TransportSection
          :copy="copy"
          :media="config.media.transport"
          :watermark-image="config.media.storeWatermark"
        />
        <ContactSection
          :copy="copy"
          :channels="config.contact"
          :icons="config.media.contactIcons"
          :sprites="config.media.contactSprites"
          :watermark-image="config.media.storeWatermark"
        />
      </main>
      <AppFooter :config="config" :copy="copy" />
      <FloatingPlayCta
        :label="copy.floatingCta.label"
        :href="config.links.floatingCta"
        :loading="copy.ctaLoading"
      />
      <AiAssistant :config="config" :locale="locale" />
    </template>

    <VideoDebugPanel v-if="videoDebugEnabled" />
  </div>
</template>
